import { HOME_PAGE } from './site-games';
import type { SitePage } from './site-pages-api';

/**
 * The Games tab's page tree: the site's section and category pages (Homepage, Casino, Live Casino, Slots, ...) are its
 * top pages, each with the single-list pages under it (Live Casino: Blackjack, Roulette, ...). Pure helpers only.
 */

/** A top page and the single-list pages under it, both in the service's order. */
export type SitePageGroup = { page: SitePage; lists: SitePage[] };

/** Whether `prefix` is `path` or one of its ancestors at a segment boundary ("/slots" of "/slots/x", not of
 *  "/slotsx"). The home page is nobody's prefix here: it is the last resort, not a match. */
const underPath = (prefix: string, path: string): boolean => prefix !== HOME_PAGE && path.startsWith(`${prefix}/`);

/**
 * The top page a list page belongs under. The service's `parent` when it names a top page; else the
 * service's own rule, so a list page is never dropped: the longest top path it sits under, else "/casino"
 * when the site has it, else the home page.
 */
const parentOf = (list: SitePage, tops: SitePage[]): string => {
  if (list.parent && tops.some((top) => top.path === list.parent)) return list.parent;
  const under = tops.filter((top) => underPath(top.path, list.path)).sort((a, b) => b.path.length - a.path.length)[0];
  if (under) return under.path;
  return tops.some((top) => top.path === '/casino') ? '/casino' : HOME_PAGE;
};

/** The pages grouped for the tree: every page that is not a single-list one (a top page), each with its list pages. */
export const groupSitePages = (pages: SitePage[]): SitePageGroup[] => {
  const tops = pages.filter((p) => p.kind !== 'list');
  const groups = tops.map((page) => ({ page, lists: [] as SitePage[] }));
  for (const list of pages.filter((p) => p.kind === 'list')) {
    const parent = parentOf(list, tops);
    groups.find((group) => group.page.path === parent)?.lists.push(list);
  }
  return groups;
};

/**
 * The pages offered once the site's navigation is known, in the service's order: each section or category page `keep`
 * holds for, with the list pages its menu links; and each list page `keep` holds for itself, with the top page it
 * sits under (see `parentOf`) but not that page's other list pages. A list page reached only from a page the site links
 * from nowhere is reached from nowhere too; one its page's menu does not link (`inMenu` false: playavenue's "Otro", which
 * only the hidden /exclusive-games menu links) is reached only if `keep` holds for it; and one offered without the page
 * it sits under would be filed under a page it does not belong to.
 */
export const offeredSitePages = (pages: SitePage[], keep: (page: SitePage) => boolean): SitePage[] => {
  const tops = pages.filter((p) => p.kind !== 'list');
  const kept = new Set(tops.filter(keep).map((p) => p.path));
  const keptLists = new Set(pages.filter((p) => p.kind === 'list' && keep(p)).map((p) => p.path));
  const holders = new Set(pages.filter((p) => keptLists.has(p.path)).map((p) => parentOf(p, tops)));
  return pages.filter((p) =>
    p.kind === 'list'
      ? keptLists.has(p.path) || (kept.has(parentOf(p, tops)) && p.inMenu !== false)
      : kept.has(p.path) || holders.has(p.path),
  );
};

/** The group a page is shown in: the one it heads, or the one it is a list page of. */
export const groupOf = (path: string, groups: SitePageGroup[]): SitePageGroup | undefined =>
  groups.find((group) => group.page.path === path || group.lists.some((list) => list.path === path));
